/**
 * Раздел «Инструкция» (help-page): инструкция для клиента-компании по работе с кабинетом —
 * адаптация контента, присланного заказчиком (10 разделов + частые вопросы, версия от
 * 07.09.2026), включая скриншоты макета (`src/assets/help/`, перенесены по решению
 * заказчика — это снимки тестового прогона, не продакшен-данные). Доступно только роли
 * company — гарантируется роутером (см. routes.tsx); контент про комиссию СОУТ, структуру
 * компании и профриски физлицу не подходит.
 *
 * Вне объёма этой итерации (см. openspec/changes/help-page/proposal.md): блок контактов
 * персонального менеджера (в системе нет привязки клиента к менеджеру Центрики) и печать/PDF
 * всей инструкции разом.
 */
import { useState, type ReactNode } from 'react'
import { PageHead } from '@/design-system/components/layout/page-head'
import { Card } from '@/design-system/components/layout/card'
import { Panel } from '@/design-system/components/layout/panel'
import { Alert } from '@/design-system/components/feedback/alert'
import { Button } from '@/design-system/components/primitives/button'
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/design-system/components/primitives/tabs'
import {
  DataTable,
  DataTableBody,
  DataTableCell,
  DataTableHead,
  DataTableHeaderCell,
  DataTableRow,
} from '@/design-system/components/data/data-table'
import { cn } from '@/lib/utils'
import settingsImg from '@/assets/help/settings.jpg'
import ordersNewImg from '@/assets/help/orders-new.jpg'
import soutStep1Img from '@/assets/help/sout-step1.jpg'
import soutStep4Img from '@/assets/help/sout-step4.jpg'
import trainingStep2Img from '@/assets/help/training-step2.jpg'
import trainingPickImg from '@/assets/help/training-pick.jpg'
import trainingStep3Img from '@/assets/help/training-step3.jpg'
import trainingSelectedImg from '@/assets/help/training-selected.jpg'
import trainingStep4Img from '@/assets/help/training-step4.jpg'
import profStep1Img from '@/assets/help/prof-step1.jpg'
import orderCardImg from '@/assets/help/order-card.jpg'
import notificationsImg from '@/assets/help/notifications.jpg'
import documentsImg from '@/assets/help/documents.jpg'
import otImg from '@/assets/help/ot.jpg'
import trainingJournalImg from '@/assets/help/training-journal.jpg'
import soutTableImg from '@/assets/help/sout-table.jpg'
import staffBookImg from '@/assets/help/staff-book.jpg'
import staffAddImg from '@/assets/help/staff-add.jpg'
import staffStructureImg from '@/assets/help/staff-structure.jpg'

type SectionId =
  | 'intro'
  | 'login'
  | 'first-order'
  | 'sout'
  | 'training'
  | 'profrisks'
  | 'tracking'
  | 'deadlines'
  | 'ecology'
  | 'staff'
  | 'faq'

/** Номер раздела (`.mi .n`/`h2 .n` макета) — «О кабинете» без числа, знак «?». */
const SECTIONS: { id: SectionId; label: string; number: string }[] = [
  { id: 'intro', label: 'О кабинете', number: '?' },
  { id: 'login', label: 'Вход', number: '1' },
  { id: 'first-order', label: 'Первый заказ', number: '2' },
  { id: 'sout', label: 'Заказ спецоценки', number: '3' },
  { id: 'training', label: 'Заказ обучения', number: '4' },
  { id: 'profrisks', label: 'Заказ профрисков', number: '5' },
  { id: 'tracking', label: 'Ход заказа и документы', number: '6' },
  { id: 'deadlines', label: 'Где смотреть сроки', number: '7' },
  { id: 'ecology', label: 'Экология', number: '8' },
  { id: 'staff', label: 'Сотрудники и структура', number: '9' },
  { id: 'faq', label: 'Частые вопросы', number: '10' },
]

/**
 * Иллюстрация шага из присланного заказчиком макета (7 сентября 2026). Скриншоты — с
 * тестового прогона (аккаунт ООО «ЦЕНТРИКА» с demo-сотрудниками), не продакшен-данные, но по
 * решению заказчика перенесены как есть — заменить на чистые предстоит отдельным заходом,
 * когда снимки понадобится обновлять.
 */
function Screenshot({
  src,
  alt,
  caption,
  size = 'full',
}: {
  src: string
  alt: string
  caption: string
  size?: 'full' | 'small' | 'narrow'
}) {
  return (
    <figure className="mb-4">
      <img
        src={src}
        alt={alt}
        className={cn(
          'mx-auto block rounded-12 border border-line',
          size === 'small' && 'w-4/5',
          size === 'narrow' && 'w-2/5',
          size === 'full' && 'w-full',
        )}
      />
      <figcaption className="mt-1.5 text-center text-12 text-txt-tertiary">{caption}</figcaption>
    </figure>
  )
}

/** Таблица «Что подготовить» (`table` макета) — двухколоночный чек-лист перед заказом услуги. */
function PrepTable({
  title,
  columnHeader = 'Где пригодится',
  rows,
}: {
  title: string
  columnHeader?: string
  rows: [what: string, where: string][]
}) {
  return (
    <Panel className="mb-4">
      <DataTable>
        <DataTableHead>
          <DataTableRow>
            <DataTableHeaderCell>{title}</DataTableHeaderCell>
            <DataTableHeaderCell>{columnHeader}</DataTableHeaderCell>
          </DataTableRow>
        </DataTableHead>
        <DataTableBody>
          {rows.map(([what, where]) => (
            <DataTableRow key={what}>
              <DataTableCell>{what}</DataTableCell>
              <DataTableCell className="text-txt-secondary">{where}</DataTableCell>
            </DataTableRow>
          ))}
        </DataTableBody>
      </DataTable>
    </Panel>
  )
}

/**
 * Кружок с номером раздела на вкладке (`.mi .n` макета) — активный залит акцентом.
 * `aria-hidden` — декоративное дублирование порядка вкладки, не должно попадать в её
 * доступное имя (иначе имя вкладки «Частые вопросы» станет «9 Частые вопросы» для AT).
 */
function TabNumber({ active, children }: { active: boolean; children: ReactNode }) {
  return (
    <span
      aria-hidden="true"
      className={cn(
        'flex size-5 shrink-0 items-center justify-center rounded-round text-11 font-bold',
        active ? 'bg-accent text-white' : 'bg-gray-bg text-gray',
      )}
    >
      {children}
    </span>
  )
}

/**
 * Постраничный переход между разделами (`.pager` макета) — «Назад» к предыдущему разделу,
 * «Далее» к следующему; на последнем разделе — «К началу». Один переключатель на всю страницу
 * (не по одному на раздел, как в статичном макете): виден только раздел активной вкладки, так
 * что он всегда оказывается под текущим содержимым.
 */
function SectionPager({
  current,
  onNavigate,
}: {
  current: SectionId
  onNavigate: (id: SectionId) => void
}) {
  const index = SECTIONS.findIndex((s) => s.id === current)
  const prev = index > 0 ? SECTIONS[index - 1] : null
  const next = index < SECTIONS.length - 1 ? SECTIONS[index + 1] : null
  const isLast = index === SECTIONS.length - 1

  return (
    <div className="mt-6 flex items-center justify-between gap-3">
      {prev ? (
        <Button variant="secondary" onClick={() => onNavigate(prev.id)}>
          ← {prev.label}
        </Button>
      ) : (
        <span />
      )}
      {isLast ? (
        <Button onClick={() => onNavigate(SECTIONS[0].id)}>К началу ↑</Button>
      ) : next ? (
        <Button onClick={() => onNavigate(next.id)}>{next.label} →</Button>
      ) : null}
    </div>
  )
}

/** Ссылка на другой раздел инструкции — переключает вкладку, не покидая страницу. */
function SectionLink({
  to,
  onNavigate,
  children,
}: {
  to: SectionId
  onNavigate: (id: SectionId) => void
  children: ReactNode
}) {
  return (
    <button
      type="button"
      onClick={() => onNavigate(to)}
      className="font-medium text-accent-txt underline underline-offset-2 hover:no-underline"
    >
      {children}
    </button>
  )
}

function IntroSection({ onNavigate }: { onNavigate: (id: SectionId) => void }) {
  return (
    <>
      <Card className="mb-4">
        <p className="mb-2">
          <b>Личный кабинет</b> нужен, чтобы заказывать услуги Центрики без анкет в Excel и
          переписки по почте, видеть сроки по сотрудникам и хранить все документы в одном месте.
        </p>
        <ul className="ml-4 list-disc space-y-1">
          <li>
            Заказы обучения, специальной оценки условий труда (СОУТ) и оценки профессиональных
            рисков в несколько шагов.
          </li>
          <li>
            Сроки обучения, СОУТ и оценки рисков по каждому сотруднику и рабочему месту, напоминания
            на почту.
          </li>
          <li>
            Договоры, протоколы, удостоверения и отчёты по всем заказам в едином реестре документов.
          </li>
        </ul>
      </Card>
      {/* Акцент «бесплатно» — зелёный, как в макете (`.free`): не предупреждение и не подсказка,
          поэтому не Alert — стилизованная пилюля на токенах success-палитры (см. Badge variant="green"). */}
      <p className="mb-4 inline-block rounded-10 border border-green bg-green-bg px-3.5 py-2 font-semibold text-green">
        Кабинет бесплатный. Работает в браузере, устанавливать ничего не нужно.
      </p>
      {/* Оглавление (`.toc` макета) — ссылки переключают вкладку, не покидая страницу. */}
      <Card>
        <h3 className="mb-2 text-12 font-semibold uppercase tracking-wide text-txt-tertiary">
          Разделы инструкции
        </h3>
        <ol className="grid grid-cols-1 gap-x-8 gap-y-1.5 nw:grid-cols-2">
          {SECTIONS.filter((s) => s.id !== 'intro').map((s) => (
            <li key={s.id}>
              <SectionLink to={s.id} onNavigate={onNavigate}>
                {s.label}
              </SectionLink>
            </li>
          ))}
        </ol>
      </Card>
    </>
  )
}

function LoginSection() {
  return (
    <>
      <Card className="mb-4">
        <ol className="list-decimal space-y-3 pl-5">
          <li>
            <b>Доступ создаёт ваш менеджер Центрики.</b> На вашу почту приходит письмо-приглашение
            со ссылкой на кабинет и логином. Пароль менеджер сообщает отдельно.
          </li>
          <li>
            <b>Войдите по ссылке из письма.</b> Кабинет открывается в браузере на компьютере.
          </li>
          <li>
            <b>Смените пароль</b> в разделе «Настройки», блок «Смена пароля»: введите текущий пароль
            и новый.
          </li>
        </ol>
      </Card>
      <Alert variant="info" className="mb-4">
        <span className="text-accent-txt">
          Сохраните адрес кабинета в закладки браузера. Почту для входа меняет менеджер, напишите
          ему, если она изменилась.
        </span>
      </Alert>
      <Screenshot
        src={settingsImg}
        alt="Раздел «Настройки»"
        size="small"
        caption="Раздел «Настройки»: профиль, смена пароля, реквизиты организации и контактные лица."
      />
    </>
  )
}

function FirstOrderSection({ onNavigate }: { onNavigate: (id: SectionId) => void }) {
  return (
    <>
      <Card className="mb-4">
        <p>
          <b>Заполнять структуру компании заранее не нужно.</b> Всё, что вы укажете в заказе,
          сохранится в структуре компании и подставится в следующие заказы. С каждым заказом кабинет
          наполняется сам.
        </p>
      </Card>
      <Card className="mb-4">
        <ol className="list-decimal space-y-3 pl-5">
          <li>
            <b>Проверьте реквизиты.</b> «Настройки» → «Реквизиты организации». Кнопка «Заполнить по
            ИНН» подтянет данные из реестра. Проверьте руководителя и «Email для документов»: они
            попадают в заявки.
          </li>
          <li>
            <b>Создайте заказ.</b> В разделе «Мои заказы» нажмите «Новый заказ», выберите услугу и
            нажмите «Создать заказ». Тот же результат даёт кнопка «Заказать…» в разделах «Обучение»,
            «Спецоценка» и «Профриски».
          </li>
          <li>
            <b>Пройдите мастер по шагам.</b> Черновик сохраняется автоматически: можно закрыть
            кабинет и вернуться позже через «Мои заказы» → «Продолжить».
          </li>
          <li>
            <b>Проверьте и отправьте.</b> На последнем шаге сверьте данные и нажмите «Отправить».
            Менеджер проверит данные, при вопросах свяжется с вами, затем подготовит предложение и
            договор.
          </li>
        </ol>
      </Card>
      <Screenshot
        src={ordersNewImg}
        alt="Новый заказ"
        size="small"
        caption="«Мои заказы» → «Новый заказ»: выберите услугу и нажмите «Создать заказ»."
      />
      <Alert variant="info" className="mb-4">
        <span className="text-accent-txt">
          Черновик появляется в списке сразу после создания. Ненужный черновик удаляется кнопкой
          «Удалить» на карточке заказа.
        </span>
      </Alert>
      <p className="text-txt-secondary">
        Что происходит внутри каждого заказа, описано в трёх следующих разделах:{' '}
        <SectionLink to="sout" onNavigate={onNavigate}>
          спецоценка
        </SectionLink>
        ,{' '}
        <SectionLink to="training" onNavigate={onNavigate}>
          обучение
        </SectionLink>
        ,{' '}
        <SectionLink to="profrisks" onNavigate={onNavigate}>
          профриски
        </SectionLink>
        .
      </p>
    </>
  )
}

function SoutSection() {
  return (
    <>
      <Card className="mb-4">
        <p>
          Анкета спецоценки заполняется по шагам, черновик сохраняется автоматически. Начать можно
          кнопкой «Заказать СОУТ» в разделе «Спецоценка», кнопкой «Заказать» в строке рабочего места
          или через «Новый заказ».
        </p>
      </Card>
      <Card className="mb-4">
        <ol className="list-decimal space-y-3 pl-5">
          <li>
            <b>Организация.</b> Реквизиты подставляются из профиля. Правки на этом шаге применяются
            и к профилю компании, и к заказу.
          </li>
          <li>
            <b>Комиссия.</b> Председатель и члены комиссии. Обычно комиссию возглавляет руководитель
            или его заместитель. Вместе с председателем — нечётное число человек: 1, 3, 5…
          </li>
          <li>
            <b>Ответственный.</b> Выберите сотрудника, укажите мобильный телефон и почту. С этим
            человеком эксперт согласует график замеров, на почту придут результаты.
          </li>
          <li>
            <b>Рабочие места.</b> «Из структуры компании» подставит подразделение, должность и ФИО.
            «Добавить вручную» — для сотрудников, которых ещё нет в кабинете. «Копия» размножает
            похожие места: данные повторятся, ФИО и СНИЛС заполняются заново. Анкету, заполненную в
            Excel по форме «Анкета и форма СОУТ», загрузите кнопкой «Загрузить из файла». По каждому
            месту укажите адрес, режим работы, оборудование и материалы, сведения о прошлой
            спецоценке.
          </li>
          <li>
            <b>Проверка.</b> Сводка по всем шагам с кнопками «Изменить». Поставьте согласие на
            обработку персональных данных и нажмите «Отправить».
          </li>
        </ol>
      </Card>
      <Screenshot
        src={soutStep1Img}
        alt="Заказ СОУТ, шаг 1"
        size="small"
        caption="Первый шаг заказа СОУТ: список «Что подготовить» и данные компании."
      />
      <Screenshot
        src={soutStep4Img}
        alt="Заказ СОУТ, рабочие места"
        caption="Шаг «Рабочие места»: три способа добавить место, у каждого места кнопки «Открыть», «Копия», «Убрать»."
      />
      <PrepTable
        title="Что подготовить к заказу СОУТ"
        rows={[
          ['Реквизиты организации', 'Шаг «Организация», подставляются из профиля'],
          ['Состав комиссии: председатель и члены', 'Шаг «Комиссия»'],
          ['Ответственный: ФИО, должность, телефон, почта', 'Шаг «Ответственный»'],
          [
            'По каждому рабочему месту: подразделение, адрес, должность, СНИЛС сотрудника, режим работы, оборудование и материалы',
            'Шаг «Рабочие места»',
          ],
          ['Сведения о прошлой спецоценке, если она проводилась', 'Карточка рабочего места'],
        ]}
      />
      <Alert variant="info">
        <span className="text-accent-txt">
          После отправки заказ проходит этапы: заявка, договор, замеры, экспертное заключение. Ход
          работ виден в карточке заказа, отчёт появится в разделе «Документы».
        </span>
      </Alert>
    </>
  )
}

function TrainingSection() {
  return (
    <>
      <Card className="mb-4">
        <p>
          Начать можно кнопкой «Заказать обучение» на главной странице, в разделе «Обучение» или
          через «Новый заказ», а из карточки сотрудника — кнопкой «Обучить сотрудника». Черновик
          появляется сразу и сохраняется автоматически. Заказ проходит четыре шага; сначала вы
          отправляете менеджеру заявку — программы и число слушателей, — затем заполняете данные
          слушателей и подтверждаете их.
        </p>
        <p className="mt-2">
          Если у сотрудников есть просроченное или истекающее в ближайшие три месяца обучение,
          кабинет предложит заполнить заявку по ним — сотрудников и программы потом можно добавить
          или убрать. Из карточки сотрудника — по его программам; в любом случае сотрудник будет
          сам добавляться и в каждую программу, которую вы выберете.
        </p>
      </Card>
      <Card className="mb-4">
        <ol className="list-decimal space-y-3 pl-5">
          <li>
            <b>Данные компании.</b> Проверьте реквизиты, при необходимости нажмите «Изменить».
          </li>
          <li>
            <b>Программы и сотрудники.</b> В одной заявке можно выбрать все необходимые программы и
            всех сотрудников, которых нужно сейчас обучить. Каталог разбит на разделы — нажмите на
            раздел, чтобы раскрыть его, «Свернуть все» сворачивает их обратно; первыми идут
            популярные пакеты программ. Поиск находит программу по названию, коду и по тому, кто
            подлежит обучению; рядом — фильтры по документу, часам и периодичности. У программы —
            плашка итогового документа, название, часы и индекс; кнопка «Инфо» справа от названия
            показывает, кто подлежит обучению, периодичность и требования к образованию. Нажмите «Добавить» и
            укажите число слушателей. Называть сотрудников сразу не обязательно: их можно выбрать
            сейчас («Выбрать сотрудников») или позже. Для нового сотрудника достаточно ФИО и
            должности. Список слушателей в Excel загружается кнопкой «Загрузить из файла»; шаблон с
            выпадающим списком программ — кнопка «Скачать шаблон». Если программа в файле не
            подобралась, сотрудник всё равно загрузится — программу выберите в каталоге. Нажмите
            «Отправить заявку» — менеджер посчитает стоимость и пришлёт договор и счёт.
          </li>
          <li>
            <b>Заполнение данных.</b> Укажите всех слушателей и заполните у каждого СНИЛС, дату
            рождения, пол, образование и e-mail. Скачайте шаблон согласия на обработку персональных
            данных, подпишите его с сотрудником и загрузите скан или фото к его фамилии. Документ об
            образовании нужен только там, где программа требует высшего или среднего
            профессионального образования: тогда у слушателя появится поле для него. Цветная метка
            показывает, всё ли заполнено.
          </li>
          <li>
            <b>Подтверждение.</b> Проверьте состав заказа и нажмите «Подтвердить данные» — заказ
            уйдёт менеджеру на проверку. Пока заполнено не всё, вместо кнопки видно, сколько осталось
            заполнить.
          </li>
        </ol>
      </Card>
      <Screenshot
        src={trainingStep2Img}
        alt="Заказ обучения, шаг 2"
        caption="Шаг «Программы и сотрудники»: подсказка, поиск и разделы каталога — сначала все свёрнуты."
      />
      <Screenshot
        src={trainingPickImg}
        alt="Окно выбора сотрудников"
        caption="Окно выбора сотрудников: отметьте людей вручную или нажмите «Выбрать просроченных»."
      />
      <Screenshot
        src={trainingSelectedImg}
        alt="Выбранные программы"
        size="small"
        caption="Выбранные программы: число слушателей, названные сотрудники и «Указано 1 из 2», если назвали не всех."
      />
      <Screenshot
        src={trainingStep3Img}
        alt="Заказ обучения, шаг 3"
        caption="Шаг «Заполнение данных»: шаблон согласия, метки заполненности и данные каждого слушателя."
      />
      <Screenshot
        src={trainingStep4Img}
        alt="Заказ обучения, шаг 4"
        size="small"
        caption="Шаг «Подтверждение»: состав заказа и кнопка «Подтвердить данные»."
      />
      <Card className="mb-4">
        <p>
          <b>Пока заявка у менеджера,</b> заказ в списке «Мои заказы» отмечен «На заполнении», а на
          карточке видно, сколько слушателей уже заполнено. Договор и счёт появятся в блоке «Договор
          и счёт» заявки, о них придёт уведомление. Слушателей на свободные места можно назвать
          сразу, а добавить или убрать программу, изменить число мест, убрать или заменить слушателя
          — кнопкой «Изменить состав»: такие изменения согласует Центрика. Согласие сохраняется в
          карточке сотрудника: в следующих заказах оно подтянется само.
        </p>
        <p className="mt-2">
          <b>Абонентское обслуживание.</b> Кнопки «Отправить заявку» нет: договор уже заключён.
          Сразу заполните данные и нажмите «Отправить заказ».
        </p>
      </Card>
      <PrepTable
        title="Что подготовить к заказу обучения"
        columnHeader="Зачем"
        rows={[
          ['Список программ и число слушателей по каждой', 'Заявка менеджеру на расчёт'],
          ['ФИО и должности слушателей', 'Выбор или добавление сотрудников'],
          ['СНИЛС, дата рождения, образование и e-mail каждого', 'Заполнение данных слушателей'],
          [
            'Подписанные согласия на обработку персональных данных',
            'Шаблон скачивается на шаге «Заполнение данных», скан или фото загружается к фамилии',
          ],
          [
            'Дипломы о высшем или среднем профессиональном образовании',
            'Только для программ, которые этого требуют',
          ],
        ]}
      />
      <Alert variant="info">
        <span className="text-accent-txt">
          После подтверждения заказ проходит этапы: заявка, договор, обучение, итоговый документ
          (при абонентском обслуживании — без договора). Протоколы и удостоверения появятся в
          карточке заказа и в разделе «Документы». Когда обучение по программе завершено, придёт
          уведомление со списком обученных сотрудников.
        </span>
      </Alert>
    </>
  )
}

function ProfrisksSection() {
  return (
    <>
      <Card className="mb-4">
        <p>
          Начать можно кнопкой «Заказать оценку» в разделе «Профриски», кнопкой «Оценить» в строке
          должности или через «Новый заказ». Оценка идёт по должностям, сколько бы людей их ни
          занимали.
        </p>
      </Card>
      <Card className="mb-4">
        <ol className="list-decimal space-y-3 pl-5">
          <li>
            <b>Организация.</b> Реквизиты и контактное лицо: ФИО, телефон, e-mail.
          </li>
          <li>
            <b>Анкета предприятия.</b> Аптечка, план эвакуации, обучение по охране труда,
            инструкции: ответы «да» или «нет». Список инструкций по охране труда можно загрузить
            файлами или ввести названия вручную.
          </li>
          <li>
            <b>Должности.</b> Подразделение, должность, число штатных единиц, выезды на автомобиле,
            оборудование. Анкету в Excel по форме «Анкета и форма (РИСКИ)» можно загрузить кнопкой
            «Загрузить из файла».
          </li>
          <li>
            <b>Проверка.</b> Сверьте данные и отправьте заказ.
          </li>
        </ol>
      </Card>
      <Screenshot
        src={profStep1Img}
        alt="Заказ профрисков, шаг 1"
        size="small"
        caption="Первый шаг заказа оценки профрисков: список «Что подготовить», данные компании и контактное лицо."
      />
      <PrepTable
        title="Что подготовить к заказу профрисков"
        rows={[
          ['Реквизиты организации', 'Шаг «Организация», подставляются из профиля'],
          [
            'Сведения по организации: аптечка, план эвакуации, обучение по ОТ, инструкции',
            'Шаг «Анкета»',
          ],
          ['Список инструкций по охране труда: файлы или названия', 'Шаг «Анкета»'],
          [
            'По каждой должности: подразделение, число штатных единиц, выезды на авто, оборудование',
            'Шаг «Должности»',
          ],
        ]}
      />
      <Alert variant="info">
        <span className="text-accent-txt">
          После отправки заказ проходит этапы: заявка, договор, оценка, карты рисков. Карты рисков
          появятся в карточке заказа и в разделе «Документы».
        </span>
      </Alert>
    </>
  )
}

function TrackingSection() {
  return (
    <>
      <Card className="mb-4">
        <p className="mb-2">
          <b>«Мои заказы»</b> показывает карточку каждого заказа: номер, статус, этап и дату. Над
          списком — фильтры по услуге («Все услуги»), статусу («Любой статус») и этапу («Любой
          этап»; этап можно выбрать и без услуги). Статусы:
        </p>
        <ul className="ml-4 list-disc space-y-1">
          <li>
            <b>Черновик</b>: заказ ещё не отправлен, его можно продолжить или удалить.
          </li>
          <li>
            <b>На заполнении</b>: заявка на обучение у менеджера, а вы заполняете данные слушателей.
          </li>
          <li>
            <b>Требуются правки</b>: менеджер вернул заказ с комментарием — исправьте и отправьте
            заново.
          </li>
          <li>
            <b>Выполняется</b>: заказ у Центрики в работе, этап виден в карточке.
          </li>
          <li>
            <b>Завершён</b>: итоговые документы готовы, их можно скачать.
          </li>
          <li>
            <b>Отменён</b>: заказ отменили вы или Центрика. Заказ, который вы отменили сами, можно
            вернуть кнопкой «Восстановить».
          </li>
        </ul>
        <p className="mt-2">
          Отправленный и ещё не завершённый заказ можно отменить кнопкой «Отменить заказ» на
          карточке или на странице заказа (у заказа обучения — внизу, рядом с реквизитами); заказ
          «На заполнении» или «Требуются правки» — и в его
          заявке (анкете). Причину можно указать — её увидит менеджер. Черновик не отменяется, его
          можно удалить.
        </p>
        <p className="mt-2">
          Состав отправленной заявки на обучение меняют кнопкой «Изменить состав» в шапке страницы
          заказа — до выпуска итоговых документов, изменения согласует Центрика. Если изменения предложила
          Центрика, на странице заказа появится блок «Изменения состава на согласовании»: нажмите
          «Согласовать» или «Отклонить» (причину можно указать). Данные новых слушателей заполняются
          прямо в окне изменения. Все изменения и решения по ним — в блоке «История изменений
          состава» на странице заказа.
        </p>
      </Card>
      <Card className="mb-4">
        <p className="mb-2">
          <b>Откройте заказ:</b> «Ход работ» под заголовком заказа показывает, на каком этапе заказ
          и какие этапы впереди. Рядом с «Выгрузить в Excel» — кнопка открытия заявки или анкеты.
        </p>
        <ul className="ml-4 list-disc space-y-1">
          <li>Спецоценка: Заявка → Договор → Замеры → Экспертное заключение</li>
          <li>Обучение: Заявка → Договор → Обучение → Итоговый документ</li>
          <li>Профриски: Заявка → Договор → Оценка → Карты рисков</li>
        </ul>
        <p className="mt-2">
          Документы по заказу появляются в карточке по мере готовности. В заказе обучения итоговый
          документ скачивается кнопкой «Итоговый документ» в строке программы. Реквизиты на дату
          создания заказа свёрнуты — нажмите на заголовок, чтобы развернуть. Кнопка «Выгрузить в
          Excel» сохраняет состав заказа таблицей.
        </p>
      </Card>
      <Screenshot
        src={orderCardImg}
        alt="Карточка заказа"
        caption="Карточка заказа: кнопки в шапке, «Ход работ» с этапами и состав заказа."
      />
      <Card className="mb-4">
        <p className="mb-2">
          <b>Уведомления.</b> На почту приходят письма «В заказ добавлен документ» (например,
          договор и счёт), «Заказ принят в работу», «Возврат на правки», «Заказ готов», «Обучение по
          программе завершено», а также напоминания о повторном обучении и о просрочке. Те же
          уведомления собраны под значком колокольчика в верхней панели кабинета; вкладки «Сроки» и
          «Заказы» отделяют напоминания о сроках от хода заказов.
        </p>
        <p className="mb-2">
          <b>Вопросы в Центрику.</b> Раздел «Мои вопросы» — под «Мои заказы». Нажмите «Задать
          вопрос», укажите тему и текст, при необходимости прикрепите файлы (PDF, JPG или PNG — до 5
          файлов, вместе до 25 МБ). Вопрос по конкретному заказу задают кнопкой «Задать вопрос» на
          странице заказа — его получит ответственный за заказ. Ответ придёт в раздел и на почту, а
          на значке конвертика рядом с колокольчиком появится точка. В вопрос можно дописать
          уточнение, а когда ответ получен — нажать «Закрыть вопрос».
        </p>
        <p>
          <b>Реквизиты фиксируются на дату заказа.</b> Если реквизиты компании изменятся позже, уже
          созданные заказы и документы по ним остаются без изменений.
        </p>
      </Card>
      <Screenshot
        src={notificationsImg}
        alt="Уведомления"
        size="narrow"
        caption="Список уведомлений под колокольчиком."
      />
      <Card className="mb-4">
        <p>
          <b>Раздел «Документы»</b>: единый реестр документов вашей организации. Отчёты СОУТ,
          протоколы об обучении, удостоверения, согласия на обработку персональных данных, договоры.
          Фильтр «Все типы» отбирает нужный тип, кнопка «Скачать» сохраняет файл.
        </p>
      </Card>
      <Screenshot
        src={documentsImg}
        alt="Раздел «Документы»"
        caption="Раздел «Документы»: реестр по типам с датами и кнопкой «Скачать»."
      />
    </>
  )
}

function DeadlinesSection() {
  return (
    <>
      <Card className="mb-4">
        <ul className="ml-4 list-disc space-y-3">
          <li>
            <b>Главная.</b> Светофор по обучению: «Просрочено», «Истекает скоро», «Всё в порядке»,
            «Нет данных», рядом ближайшие сроки, заказы и документы. Пока заказов не было, главная
            показывает «Пока нет данных».
          </li>
          <li>
            <b>Охрана труда.</b> Пульт по всем сотрудникам: в одной строке СОУТ, профриски и
            обучение. Плашки-фильтры «Просроченное обучение», «Просроченная СОУТ», уровни риска,
            «Нет данных», «Всё ок». Поиск по ФИО, подразделению и должности. Кнопка «Добавить в
            структуру» добавляет новое рабочее место и сотрудника.
          </li>
          <li>
            <b>Обучение.</b> Журнал сроков по направлениям: охрана труда, пожарная безопасность,
            экология, прочее. Нажмите на строку сотрудника, чтобы увидеть программы и сроки по
            каждой. Есть фильтры и «Выгрузить в Excel».
          </li>
          <li>
            <b>Спецоценка.</b> По каждому рабочему месту: класс условий труда, дата проведения, срок
            действия, статус. Кнопка «Заказать» прямо в строке. Внизу раздела — список заказов по
            спецоценке.
          </li>
          <li>
            <b>Профриски.</b> По каждой должности: статус оценки и уровень риска, кнопка «Оценить».
            Внизу раздела — список заказов по профрискам.
          </li>
        </ul>
      </Card>
      <Screenshot
        src={otImg}
        alt="Пульт «Охрана труда»"
        caption="Пульт «Охрана труда»: сотрудник, должность, СОУТ, профриски, обучение и фильтры одной кнопкой."
      />
      <Screenshot
        src={trainingJournalImg}
        alt="Раздел «Обучение»"
        caption="Раздел «Обучение»: светофор, фильтры и журнал сроков по направлениям."
      />
      <Screenshot
        src={soutTableImg}
        alt="Раздел «Спецоценка»"
        caption="Раздел «Спецоценка»: класс условий труда, даты и срок действия по каждому рабочему месту."
      />
      <Alert variant="info">
        <span className="text-accent-txt">
          Обучение или спецоценка проводились раньше, до кабинета? Внесите их в карточку сотрудника
          («Сотрудники» → фамилия): «Добавить историческое обучение», «Добавить историческую СОУТ» —
          и кабинет посчитает действующие сроки.
        </span>
      </Alert>
    </>
  )
}

function EcologySection() {
  return (
    <>
      <Card className="mb-4">
        <p className="mb-2">
          Раздел «Экология» — пульт экологических обязанностей организации. Кабинет держит список
          площадок, показывает по каждой, какие документы и отчётность нужны, считает сроки и
          заранее пишет письмо, пока срок не подошёл.
        </p>
        <ul className="ml-4 list-disc space-y-3">
          <li>
            <b>Шаг 1. Площадки.</b> «Основная площадка» создана с юридическим адресом — её можно
            переименовать, но не удалить. Кнопка «Добавить площадку» добавляет остальные: название и
            адрес обязательны, код и категорию НВОС (I–IV или «без категории») укажите, если знаете.
            Не знаете — оставьте пустым: категорию проставит эколог Центрики по опросу.
          </li>
          <li>
            <b>Шаг 2. Опрос.</b> Кнопка «Пройти опрос» открывает анкету по площадке: освещение,
            отходы, источники выбросов, вода, уже разработанные документы и отчётность. Отвечать на
            всё сразу не обязательно — вопрос без ответа просто ничего не добавит. По ответам
            кабинет покажет, что у вас уже есть, а что нужно сделать, и почему.
          </li>
          <li>
            <b>Шаг 3. Светофор.</b> Зелёный — действует или сдано; жёлтый — истекает в ближайшие 90
            дней, на согласовании или требует актуализации; красный — просрочено или отсутствует;
            серый — по категории площадки не требуется.
          </li>
        </ul>
      </Card>
      <Card className="mb-4" header="Что внутри площадки">
        <ul className="ml-4 list-disc space-y-3">
          <li>
            <b>Отчётность и периодические мероприятия.</b> Строки со сроком сдачи: 2-ТП, отчёт по
            программе ПЭК, плата за НВОС и декларация, журнал движения отходов. Следующий срок
            кабинет считает от последней сдачи.
          </li>
          <li>
            <b>Лабораторный контроль.</b> Отдельная вкладка для замеров: на границе СЗЗ, на
            источниках выбросов и воды. Отметьте дату последних замеров и периодичность по программе
            ПЭК на площадке — кабинет посчитает следующий срок. Протоколы замеров прикрепляются в
            карточке строки — можно несколько файлов.
          </li>
          <li>
            <b>Проектные документы и нормативы.</b> Постановка на учёт, инвентаризация выбросов,
            НДВ, НМУ, программа ПЭК, ПНООЛР, паспорта отходов, СЗЗ, паспорта ПГУ. Под строкой видно,
            откуда она взялась: «выполнила Центрика», «внесён вами» или «из опроса».
          </li>
          <li>
            <b>Свои документы.</b> Если документ уже есть, внесите его сами: нужны дата разработки и
            срок действия, файл — по желанию. Нажмите на строку — откроется её карточка с
            реквизитами. Поля карточки зависят от документа: у инвентаризации, НДВ, НМУ и СЗЗ —
            количество источников выбросов (у СЗЗ — ещё и шума; при внесении на несколько площадок —
            по каждой площадке, кабинет покажет сумму по организации), у ПЭК — количество видов
            отходов, у постановки на учёт — код объекта и категория (это те же данные, что в
            карточке площадки), у НДВ — экспертное и санитарно-эпидемиологическое заключения и
            справка о фоновых концентрациях, у СЗЗ — документы по этапам: проект, замеры на границе
            СЗЗ, решение об установлении, у паспортов ПГУ — сами паспорта, у НМУ — письмо о
            согласовании, у паспортов отходов — список отходов по ФККО (введите код или начало
            наименования — второе подставится из справочника). Файлы прикрепляются в карточке уже
            внесённой строки и сразу попадают в «Документы». Строку, которую сделала Центрика по
            заказу, правит только менеджер: вы видите её карточку для просмотра. Лишнюю строку можно
            убрать — повторный опрос её не вернёт. Если площадок много, группу площадки можно
            свернуть, нажав на её название.
          </li>
          <li>
            <b>Обучение по экологической безопасности.</b> Строка заполняется из журнала обучения:
            кто обучен и до какой даты. Заказать обучение — в разделе «Обучение».
          </li>
        </ul>
      </Card>
      <Card className="mb-4" header="Заказ, письма и сопровождение">
        <ul className="ml-4 list-disc space-y-3">
          <li>
            <b>Заказ.</b> Нажмите «Заказать» в красной или жёлтой строке — откроется мастер заказа с
            этой площадкой и услугой, ответы из опроса подставятся сами. Кнопка «Заказать…» в шапке
            подраздела («Заказать сдачу отчётности» и т.п.) показывает только услуги этого
            подраздела (в отчётности первая — «Экологическая отчётность»: если нужно сдать несколько
            отчётов или вы не знаете, какие нужны), «Заказать услугу» на странице «Экология» — все
            услуги группами, как в меню раздела; в обоих случаях услугу можно найти поиском по
            названию. Дальше — площадки (нужной площадки нет в списке — «Добавить площадку» прямо в
            мастере) с реквизитами услуги под каждой отмеченной площадкой — например, количество
            источников выбросов, виды лицензируемой деятельности, отходы для паспортов (из
            справочника ФККО или любым названием) или файлы с показателями для лабораторного
            контроля (прикрепляются сразу); их можно не заполнять, если не известно, а число и
            отходы из карточки документа площадки подставятся сами. Затем вопросы по услуге, если
            они у неё есть; комментарий менеджеру и «Отправить заявку» — на последнем шаге. После
            отправки — «Заявка принята», менеджер свяжется с вами. Ход работ в карточке заказа:
            «Принят → Сбор данных → В работе → Выполнен» (у абонентского обслуживания — без сбора
            данных). Пока Центрика работает, строка показывает «На согласовании». Готовый документ
            открывается прямо из строки и дополнительно лежит в разделе «Документы».
          </li>
          <li>
            <b>Письма о сроках.</b> За 60, 30 и 7 дней до срока и один раз после просрочки кабинет
            пишет на почту организации, контактам с галочкой «получает уведомления» и ответственному
            за экологию — его контакт указывается в «Настройках», рядом с реквизитами организации.
            Пока по строке идёт заказ, писем нет.
          </li>
          <li>
            <b>«Что менялось».</b> Раз в год кабинет спрашивает, менялись ли адрес, площадки,
            оборудование, отходы или руководитель. Изменилось раньше — нажмите «Были изменения»:
            кабинет подсветит документы, которые нужно актуализировать.
          </li>
          <li>
            <b>Сопровождение.</b> У абонентов в разделе появляется блок «Сопровождение»: тариф,
            инженер, план работ на год и отметки о выполнении. У остальных — предложение тарифов
            «Защита» и «Прайм»; в заявке заранее отмечены все площадки — лишние можно снять,
            менеджер перезвонит.
          </li>
        </ul>
      </Card>
      <Alert variant="info">
        <span className="text-accent-txt">
          Часть документов разрабатывалась до кабинета? Внесите их даты — тогда кабинет посчитает
          сроки и напомнит о них вовремя.
        </span>
      </Alert>
    </>
  )
}

function StaffSection() {
  return (
    <>
      <Card className="mb-4">
        <p className="mb-2">
          Раздел «Сотрудники» хранит структуру компании: подразделения → рабочие места → сотрудники.
          Всё, что вы указали в заказах, попадает сюда автоматически.
        </p>
        <ul className="ml-4 list-disc space-y-3">
          <li>
            <b>Адресная книга.</b> Список сотрудников по подразделениям со статусом и сроками
            обучения. Кнопка «Уволен(а)» переводит сотрудника в список уволенных: он пропадает из
            списка сотрудников, при этом остаётся в заказах, в которых участвовал. Переключатель
            «Показать деактивированных» открывает список уволенных, оттуда сотрудника можно вернуть.
          </li>
          <li>
            <b>Добавить сотрудника.</b> Обязательны ФИО, подразделение и должность. Блок «Для
            документов» можно заполнить позже: СНИЛС, дата рождения, пол, дата приёма, почта,
            телефон, образование, разряд. Эти данные подставляются в заказы, чтобы не вводить их
            каждый раз. Есть отметка для сменного рабочего места. Большие буквы в ФИО и должности
            кабинет расставляет сам. Если похожая должность уже есть в справочнике, под полем
            появится «Возможно, вы имели в виду: …» — нажмите на вариант, чтобы его подставить.
          </li>
          <li>
            <b>Структура.</b> Подразделения и рабочие места: добавляйте новые, удаляйте лишние.
          </li>
          <li>
            <b>Загрузка из файла.</b> Нажмите «Загрузить файл» и выберите заполненный шаблон
            («Скачать шаблон структуры») или свой файл — выгрузку из 1С или таблицу Excel/Word с
            подразделениями, должностями и сотрудниками. Отчёт 1С «Штатная расстановка» кабинет
            распознаёт сам. Для другого файла кабинет покажет, как понял колонки: проверьте и
            нажмите «Продолжить»; у штатного расписания вместо ФИО укажите колонку штатных единиц.
            Перед применением кабинет покажет предпросмотр: сотрудники сверяются по ФИО и дате
            рождения, по строкам без даты рождения действие выбирается вручную, а по штатным
            единицам добавляются вакантные рабочие места.
          </li>
        </ul>
      </Card>
      <Screenshot
        src={staffBookImg}
        alt="Адресная книга"
        caption="«Сотрудники» → «Адресная книга»: список по подразделениям, поиск и кнопка «Добавить сотрудника»."
      />
      <Screenshot
        src={staffAddImg}
        alt="Новый сотрудник"
        size="small"
        caption="Форма «Новый сотрудник»: обязательные поля и блок «Для документов», который можно заполнить позже."
      />
      <Screenshot
        src={staffStructureImg}
        alt="Структура компании"
        caption="«Сотрудники» → «Структура»: загрузка из Excel по шаблону и список подразделений с рабочими местами."
      />
    </>
  )
}

const FAQ: { q: string; a: ReactNode }[] = [
  {
    q: 'Сколько стоит кабинет?',
    a: 'Кабинет бесплатный. Вы платите только за заказанные услуги, как и раньше.',
  },
  {
    q: 'Нужно ли сразу заполнять всю структуру компании?',
    a: 'Нет. Первый заказ можно сделать сразу после входа. Данные из каждого заказа сохраняются в структуре и подставляются в следующие. Полную структуру можно загрузить из Excel в любой момент.',
  },
  {
    q: 'Можно ли прервать заполнение заказа?',
    a: 'Да. Черновик сохраняется автоматически. Вернитесь в «Мои заказы» и нажмите «Продолжить».',
  },
  {
    q: 'Как удалить лишний черновик?',
    a: 'Кнопка «Удалить» на карточке заказа в разделе «Мои заказы». Удалять можно только черновики.',
  },
  {
    q: 'Ошиблись в уже отправленной заявке?',
    a: 'Состав заявки на обучение меняют кнопкой «Изменить состав» на странице заказа — изменения согласует Центрика. В остальных случаях нажмите «Задать вопрос» на странице заказа: менеджер ответит или вернёт заявку на правки, вы получите письмо «Возврат на правки» и исправите данные в кабинете.',
  },
  {
    q: 'Зачем согласие на обработку персональных данных?',
    a: 'Для обучения нужно согласие каждого слушателя. Шаблон скачивается в заказе, подписанный файл загружается к фамилии сотрудника. Согласие бессрочное и хранится в карточке сотрудника, поэтому в следующих заказах загружать его заново не нужно.',
  },
  {
    q: 'Сотрудник уволился, что делать?',
    a: 'В «Адресной книге» нажмите «Уволен(а)» напротив фамилии. Сотрудник перейдёт в список уволенных и пропадёт из списка сотрудников, в прошлых заказах он остаётся. Если человек вернулся, включите «Показать деактивированных» и верните его в список.',
  },
  {
    q: 'Забыли пароль или сменилась почта?',
    a: 'Пароль: на странице входа нажмите «Забыли пароль?» — на почту придёт ссылка для нового пароля; если вы в кабинете — смените его в «Настройках». Почту для входа меняет менеджер — задайте вопрос в «Мои вопросы».',
  },
  {
    q: 'Кто видит наши данные?',
    a: 'Данные хранятся на серверах Центрики в России. Вход в кабинет создаёт ваш менеджер, случайных пользователей в нём нет.',
  },
  {
    q: 'Кому приходят письма из кабинета?',
    a: 'Письма приходят на почту организации. Чтобы уведомления получал ещё кто-то, добавьте контактное лицо в «Настройках» или сообщите менеджеру.',
  },
  {
    q: 'Чего-то не хватает в кабинете?',
    a: 'Кабинет новый, вы среди первых пользователей. Напишите менеджеру, что нравится, чего не хватает и что мешает работать: замечания попадают в доработки.',
  },
]

function FaqSection() {
  return (
    <Card>
      {FAQ.map(({ q, a }, i) => (
        <div key={q} className={i > 0 ? 'mt-4 border-t border-line pt-4' : undefined}>
          <p className="font-semibold text-txt">{q}</p>
          <p className="text-txt-secondary">{a}</p>
        </div>
      ))}
    </Card>
  )
}

export function HelpPage() {
  const [section, setSection] = useState<SectionId>('intro')

  return (
    // Читательская колонка уже рабочей области (по образцу max-w-narrow — тот же токен
    // --bp-narrow, что и в фильтрах ot-overview/positions): без неё скриншоты и текст растягиваются
    // на всю ширину dashboard-разметки и раздуваются сильнее, чем в присланном макете (max-width: 1040px).
    <div className="max-w-narrow">
      <PageHead title="Инструкция" subtitle="Первые шаги, заказы, сроки и ответы на вопросы" />
      <Tabs value={section} onValueChange={(v) => setSection(v as SectionId)}>
        <TabsList aria-label="Разделы инструкции" className="h-auto flex-wrap">
          {SECTIONS.map((s) => (
            <TabsTrigger key={s.id} value={s.id} className="inline-flex items-center gap-2">
              <TabNumber active={s.id === section}>{s.number}</TabNumber>
              {s.label}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="intro">
          <IntroSection onNavigate={setSection} />
        </TabsContent>
        <TabsContent value="login">
          <LoginSection />
        </TabsContent>
        <TabsContent value="first-order">
          <FirstOrderSection onNavigate={setSection} />
        </TabsContent>
        <TabsContent value="sout">
          <SoutSection />
        </TabsContent>
        <TabsContent value="training">
          <TrainingSection />
        </TabsContent>
        <TabsContent value="profrisks">
          <ProfrisksSection />
        </TabsContent>
        <TabsContent value="tracking">
          <TrackingSection />
        </TabsContent>
        <TabsContent value="deadlines">
          <DeadlinesSection />
        </TabsContent>
        <TabsContent value="ecology">
          <EcologySection />
        </TabsContent>
        <TabsContent value="staff">
          <StaffSection />
        </TabsContent>
        <TabsContent value="faq">
          <FaqSection />
        </TabsContent>
      </Tabs>
      <SectionPager current={section} onNavigate={setSection} />
    </div>
  )
}
